import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import Home from '@/pages/home/index.vue'  // 使用@别名简化路径
import Layout from "../layout/index.vue";
import Product from '@/pages/product/index.vue'
import ProductDetail from '@/pages/product/productDetail.vue'
import xrSolution from '@/pages/solution/index.vue'
import vrSpaceSolutions from '@/pages/solution/vrSpaceSolutions.vue'
import xrImmersiveLearning from '@/pages/solution/xrImmersiveLearning.vue'
import xrHome from '@/pages/solution/bigSpaceHousehold.vue'
import lbeCustom from '@/pages/solution/lbeTheme.vue'
import xrTheater from '@/pages/solution/xrPark.vue'
import Service from '@/pages/service/index.vue'
import CaseDetail from '@/pages/service/caseDetail.vue'
import Keyword from '@/pages/keyword/index.vue'
import Contact from '@/pages/contact/index.vue'
import News from '@/pages/news/index.vue'
import NewsDetail from '@/pages/news/newsDetail.vue'
import MarketingH5 from '@/pages/marketing/MarketingH5.vue'
import LearningH5 from '@/pages/marketing/LearningH5.vue'
import { seoApi } from '@/api'

type SeoMeta = {
  title?: string
  keywords?: string
  description?: string
  // 可选布局标记：'' 为默认布局，'blank' 表示使用空白布局
  layout?: '' | 'blank'
}

const ensureMeta = (name: 'keywords' | 'description', content?: string) => {
  if (typeof document === 'undefined') return
  const value = (content || '').trim()
  if (!value) return

  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', name)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', value)
}

// 删除指定的 meta 标签
const removeMeta = (name: 'keywords' | 'description') => {
  if (typeof document === 'undefined') return
  const tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (tag) {
    tag.remove()
  }
}

// SEO 内容缓存
const seoCache = new Map<string, SeoMeta>()

/**
 * 将路由 path 转换为 API 需要的 url 参数
 * 例如: 'contact.html' -> 'contact', 'product/123.html' -> 'product'
 * 详情页特殊处理: 'service/case/123.html' -> 'service/case/123.html', 'news/456.html' -> 'news/456.html'
 */
const pathToSeoUrl = (path: string): string | null => {
  // 移除开头的斜杠
  let url = path.replace(/^\//, '')
  
  // 详情页特殊处理：保留完整路径（包含 id）
  // service/case/:id.html 或 service/case/:id -> 传递完整路径
  if (url.match(/^service\/case\/[^/]+(\.html)?$/)) {
    // 移除 .html 后缀（如果有）
    return url.replace(/\.html$/, '')
  }
  
  // news/:id.html 或 news/:id -> 传递完整路径
  if (url.match(/^news\/[^/]+(\.html)?$/)) {
    // 移除 .html 后缀（如果有）
    return url.replace(/\.html$/, '')
  }
  
  // product/:id.html 或 product/:id -> 传递完整路径
  if (url.match(/^product\/[^/]+(\.html)?$/)) {
    // 移除 .html 后缀（如果有）
    return url.replace(/\.html$/, '')
  }
  
  // 移除 .html 后缀
  url = url.replace(/\.html$/, '')
  
  // 特殊路径映射（如果需要）
  const pathMap: Record<string, string> = {
    'contact': 'cooperation', // 联系页面映射为 cooperation
    // 可以添加更多映射
  }
  
  return pathMap[url] || url || null
}

/**
 * 动态获取 SEO 内容（带超时处理）
 */
const fetchSeoContent = async (path: string, timeout: number = 5000): Promise<SeoMeta | null> => {
  // 检查缓存
  if (seoCache.has(path)) {
    const cached = seoCache.get(path)
    console.log('使用缓存的 SEO 内容:', cached)
    return cached || null
  }
  
  const url = pathToSeoUrl(path)
  console.log(`获取 SEO 内容，path: ${path}, url: ${url}`)
  if (!url) {
    console.warn('无法转换 path 为 url:', path)
    return null
  }
  
  try {
    // 添加超时处理，避免阻塞导航（增加到5秒）
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn(`获取 SEO 内容超时 (${url})`)
        resolve(null)
      }, timeout)
    })
    
    const apiPromise = seoApi.getSeoContent(url).then((response) => {
      console.log(`API 响应 (${url}):`, response)
      if (response.data) {
        const seoMeta: SeoMeta = {
          title: response.data.title,
          keywords: response.data.keyWords, // 注意：接口返回的是 keyWords
          description: response.data.description,
        }
        console.log(`转换后的 SEO Meta (${url}):`, seoMeta)
        // 缓存结果
        seoCache.set(path, seoMeta)
        return seoMeta
      }
      console.warn(`API 响应中没有 data 字段 (${url})`)
      return null
    })
    
    // 使用 Promise.race 实现超时
    const result = await Promise.race([apiPromise, timeoutPromise])
    console.log(`最终返回的 SEO 内容 (${url}):`, result)
    return result
  } catch (error) {
    console.warn(`获取 SEO 内容失败 (${url}):`, error)
  }
  
  return null
}

const routes: RouteRecordRaw[] = [
    {
      path: '/',
      name: 'Layout',
      component: Layout ,
      redirect: '/home.html',
      children: [
        // 旧路径兼容（不改变内容，但会改变地址栏）
        { path: 'home', redirect: '/home.html' },
        { path: 'product', redirect: '/product.html' },
        { path: 'solution', redirect: '/solution.html' },
        { path: 'service', redirect: '/service.html' },
        { path: 'news', redirect: '/news.html' },
        { path: 'contact', redirect: '/contact.html' },
        { path: 'keyword', redirect: '/keyword.html' },
        { path: 'xr-theater', redirect: '/xr-theater.html' },
        { path: 'xr-home', redirect: '/xr-home.html' },
        { path: 'lbe-custom', redirect: '/lbe-custom.html' },
        { path: 'vrSpaceSolutions', redirect: '/vrSpaceSolutions.html' },
        { path: 'xr-immersiveLearning', redirect: '/xr-immersiveLearning.html' },
        {
          path: 'home.html',
          name: 'Home',
          component: Home,
          meta: {
            title: 'EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'product.html',
          name: 'product',
          component: Product,
          meta: {
            title: '产品 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'product/emushow',
          name: 'product-emushow',
          component: Product,
          meta: { title: 'ENUMSHOW播控系统' }
        },
        {
          // 产品详情（新路径：/product/:id.html）
          path: 'product/:id.html',
          name: 'product-detail-html',
          component: ProductDetail,
          meta: { title: '产品详情' }
        },
        // 兼容旧路径：/product/detail/:id.html（重定向到新路径）
        {
          path: 'product/detail/:id.html',
          redirect: (to) => `/product/${to.params.id}.html`,
        },
        // 兼容旧路径：/product/detail/:id（不强制改地址栏）
        {
          path: 'product/detail/:id',
          name: 'product-detail-id-legacy',
          component: ProductDetail,
          meta: { title: '产品详情' }
        },
        {
          path: 'product/detail',
          name: 'product-detail',
          component: ProductDetail,
          meta: { title: '产品详情' }
        },
        {
          path: 'product/deepsea',
          name: 'product-deepsea',
          component: Product,
          meta: { title: '《深海启航:亚特兰蒂斯之光》' }
        },
        {
          path: 'product/ink-space',
          name: 'product-ink-space',
          component: Product,
          meta: { title: '《墨宇寻踪》' }
        },
        {
          path: 'product/dinosaur',
          name: 'product-dinosaur',
          component: Product,
          meta: { title: '《宇宙探秘:恐龙星球》' }
        },
        {
          path: 'product/planet',
          name: 'product-planet',
          component: Product,
          meta: { title: '《宇宙探秘:行星之旅》' }
        },
        {
          path: 'product/fantasy-forest',
          name: 'product-fantasy-forest',
          component: Product,
          meta: { title: '《奇幻防线:森林》' }
        },
        {
          path: 'solution.html',
          name: 'solution',
          component: xrSolution,
          meta: {
            title: '解决方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'lbe-custom.html',
          name: 'lbe-custom',
          component: lbeCustom,
          meta: {
            title: 'LBE大空间定制开发方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'vrSpaceSolutions.html',
          name: 'vrSpaceSolutions',
          component: vrSpaceSolutions,
          meta: {
            title: 'VR 大空间方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'xr-immersiveLearning.html',
          name: 'xr-immersiveLearning',
          component: xrImmersiveLearning,
          meta: {
            title: 'XR沉浸式研学方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'xr-theater.html',
          name: 'xr-theater',
          component: xrTheater,
          meta: {
            title: 'XR剧院内容及管理方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
            path: 'xr-home.html',
          name: 'xr-home',
          component: xrHome,
          meta: {
            title: 'XR大空间地产家装方案 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'service.html',
          name: 'service',
          component: Service,
            meta: {
              title: '服务案例 - EMUSHOW 易目秀',
            } satisfies SeoMeta
        },
        {
          path: 'service/case/:id.html',
          name: 'case-detail-html',
          component: CaseDetail,
          meta: { title: '案例详情' }
        },
        // 兼容旧案例详情：/service/case/:id（不强制改地址栏）
        {
          path: 'service/case/:id',
          name: 'case-detail-legacy',
          component: CaseDetail,
          meta: { title: '案例详情' }
        },
        {
          path: 'news.html',
          name: 'news',
          component: News,
          meta: {
            title: '资讯中心 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'news/:id.html',
          name: 'news-detail-html',
          component: NewsDetail,
          meta: { title: '资讯详情' }
        },
        // 兼容旧新闻详情：/news/:id（不强制改地址栏）
        {
          path: 'news/:id',
          name: 'news-detail-legacy',
          component: NewsDetail,
          meta: { title: '资讯详情' }
        },
        {
          path: 'contact.html',
          name: 'contact',
          component: Contact,
          meta: {
            title: '联系我们 - EMUSHOW 易目秀',
          } satisfies SeoMeta
        },
        {
          path: 'keyword.html',
          name: 'keyword',
          component: Keyword,
          meta: {
            title: '关键词 - EMUSHOW 易目秀',
          } satisfies SeoMeta
          },
        // 营销 H5 独立页面（入口为 /marketing.html），使用空白布局
        {
          path: 'marketing.html',
          name: 'marketing-h5',
          component: MarketingH5,
          meta: {
            title: 'EMUSHOW 易目秀',
            layout: 'blank',
          } satisfies SeoMeta,
        },
        {
          path: 'learning.html',
          name: 'learning-h5',
          component: LearningH5,
          meta: {
            title: 'EMUSHOW 易目秀',
            layout: 'blank',
          } satisfies SeoMeta,
        },
      ]
    },
]

const router = createRouter({
  history: createWebHistory(), // 或createWebHashHistory()
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // 如果有保存的位置（比如浏览器前进/后退），则恢复到该位置
    if (savedPosition) {
      return savedPosition
    }
    // 否则滚动到页面顶部
    return { top: 0 }
  }
})

// 路由守卫：动态设置页面标题
router.beforeEach(async (to, _from, next) => {
  try {
    const meta = (to.meta || {}) as SeoMeta
    
    // 新闻详情页不使用 SEO API，由组件自己设置 SEO
    const isNewsDetail = to.path.match(/^\/news\/[^/]+(\.html)?$/)
    
    // 先设置默认值，确保页面能正常显示
    document.title = meta.title || 'EMUSHOW 易目秀'
    
    if (isNewsDetail) {
      // 新闻详情页删除 keywords，不设置 description（由组件设置）
      removeMeta('keywords') // 删除 keywords meta 标签
    } else {
      // 其他页面：所有 SEO 信息都从 API 获取，不设置默认值
    }
    
    if (!isNewsDetail) {
      // 尝试从 API 获取 SEO 内容（异步，不阻塞导航）
      fetchSeoContent(to.path).then((apiSeo) => {
        console.log('API 返回的 SEO 内容:', apiSeo)
        if (apiSeo) {
          // 使用 API 返回的内容
          if (apiSeo.title && apiSeo.title.trim()) {
            document.title = apiSeo.title
            console.log('设置 title:', apiSeo.title)
          }
          if (apiSeo.keywords && apiSeo.keywords.trim()) {
            ensureMeta('keywords', apiSeo.keywords)
            console.log('设置 keywords:', apiSeo.keywords)
          }
          if (apiSeo.description && apiSeo.description.trim()) {
            ensureMeta('description', apiSeo.description)
            console.log('设置 description:', apiSeo.description)
          }
        } else {
          console.warn('API 未返回 SEO 内容')
        }
      }).catch((error) => {
        console.warn('获取 SEO 内容失败:', error)
      })
    }
    
    // 立即继续导航，不等待 API 响应
    next()
  } catch (error) {
    console.error('路由守卫错误:', error)
    // 确保即使出错也能继续导航
    next()
  }
})

export default router

